/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { DeliveryOption as DeliveryOptionData } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { ICheckoutDeliveryOption, ICheckoutDeliveryOptionEdit, ICheckoutDeliveryOptionsError, ICheckoutDeliveryOptionsList, ICheckoutDeliveryOptionsViewProps } from '../../../modules/smwe-checkout-delivery-options/./smwe-checkout-delivery-options';
import { orderSummaryShipmentCalculation } from '../../../Utilities/smwe-order-summary-shipment-calculation';
interface ICheckoutDeliveryOptionsListFiltered extends ICheckoutDeliveryOptionsList {
    globalConfig?: {
        [x: string]: unknown;
    };
    options?: DeliveryOptionData[];
    props: React.PropsWithChildren<ICheckoutDeliveryOptionsViewProps>;

}

type ModShipping = {
    id: number;
    amount: number;
}[];

const DeliveryOptionList: React.FC<ICheckoutDeliveryOptionsListFiltered> = ({
    DeliveryOptionsList,
    list,
    globalConfig,
    options,
    props
}) => {
    const cart = props.checkoutState?.checkoutCart.cart;
    if (!list || list.length === 0 || !cart) {
        return null;
    }

    let cartRequirements = orderSummaryShipmentCalculation({ cart }).summary;
    const shippingState = props.checkoutState?.shippingAddress?.State;
    if (shippingState === 'AK' || shippingState === 'HI') {
        cartRequirements = `${cartRequirements}_AKHI`;
    }

    const shippingMethodTiers = globalConfig && globalConfig.shipmentMethodTiers as {} || {};
    const allowedShippingMethodIds = shippingMethodTiers[cartRequirements] as string[] || [];
    const pennyShippingMethodIds = globalConfig?.pennyShippingIds as number[] || [];
    const modShippingMethodIds = globalConfig?.modShippingIds as ModShipping || [];
    const localList: ICheckoutDeliveryOptionEdit[] = [];
    if (options) {
        // maps options with list for sorting
        const combinedList: { option: DeliveryOptionData; list: ICheckoutDeliveryOptionEdit }[] = [];
        options.map((option, index) => combinedList.push({ option: option, list: list[index] }));
        // sort list by lowest to highest price
        const combinedListSorted = combinedList.sort((a, b) => (a.option.ShippingChargeAmount! > b.option.ShippingChargeAmount!) ? 1 : ((b.option.ShippingChargeAmount! > a.option.ShippingChargeAmount!) ? -1 : 0));
        combinedListSorted.forEach((deliveryData) => {
            if (deliveryData.list) {
                // search to find out if it is a mod shipping method
                const allowedShippingMethod = allowedShippingMethodIds.find(shippingMethodId => Number(shippingMethodId) === deliveryData.option.RecordId);
                const isModShippingMethod = modShippingMethodIds.find(modShipping => Number(modShipping.id) === deliveryData.option.RecordId);
                // backwards compatibility for penny shipping
                const isPennyShippingMethod = pennyShippingMethodIds.find(pennyShipping => Number(pennyShipping) === deliveryData.option.RecordId);

                // Filter out penny shipping methods that are declared
                // in the global config, only show shipping methods
                // that have a price of 0.01 for the customer at this current time
                // of checkout. If it's not a penny shipping method, always display
                // it.
                if (isModShippingMethod && deliveryData.option.ShippingChargeAmount === isModShippingMethod.amount) {
                    localList.push(deliveryData.list);
                } else if (isPennyShippingMethod && deliveryData.option.ShippingChargeAmount === 0.01) {
                    localList.push(deliveryData.list);
                } else if (!isModShippingMethod && !isPennyShippingMethod && allowedShippingMethod) {
                    localList.push(deliveryData.list);
                }
            }
        });
    }
    // in case options doesn't load in or undefined, display error message
    if (localList.length === 0) {
        return (
            <div className='ms-checkout-delivery-options__error'>
                {props.resources.shippingMethodConfigError}
            </div>
        );
    }

    const currentMethodAllowed = localList.find((item) => {
        return props.selectedItemId === item.code;
    });
    if (!currentMethodAllowed) {
        const event = { currentTarget: { value: localList[0].code } } as React.ChangeEvent<HTMLInputElement>;
        props.onDeliveryOptionChange(event);
    }

    return (
        <Node {...DeliveryOptionsList}>
            {localList.map((deliveryOption: ICheckoutDeliveryOptionEdit) => {
                return (<Node {...deliveryOption.DeliveryOption} key={deliveryOption.code}>
                    <label className='ms-checkout-delivery-options__label'>
                        {deliveryOption.radioButton}
                        {deliveryOption.description}
                    </label>
                    {deliveryOption.price}
                </Node>);
            })}
        </Node>
    );
};

const DeliveryOptionSelected: React.FC<ICheckoutDeliveryOption> = ({
    DeliveryOption,
    description,
    price,
}) => {

    return (
        <Node {...DeliveryOption}>
            {description}
            {price}
        </Node>
    );
};

const ErrorMessage: React.FC<ICheckoutDeliveryOptionsError> = ({
    Error,
    title,
    message,
}) => {

    return (
        <Node {...Error}>
            {title}
            {message}
        </Node>
    );
};

const CheckoutDeliveryOptionsView: React.FC<ICheckoutDeliveryOptionsViewProps> = props => {
    const { CheckoutDeliveryOptions,
        viewState,
        deliveryOptions,
        deliveryOptionsData,
        deliveryOptionSelected,
        errorMessage,
        waiting,
        saveButton,
        editButton,
        cancelButton } = props;
    return (
        // TODO: All wrapper div should be provided by viewProps. Once SDK provide the sln, update markup.
        <Module {...CheckoutDeliveryOptions}>
            {viewState.isLoading && waiting}
            {viewState.isError && errorMessage && <ErrorMessage {...errorMessage} />}
            {viewState.isShowList && deliveryOptions && <DeliveryOptionList {...deliveryOptions} globalConfig={props.context.app.config} options={deliveryOptionsData} props={props} />}
            {viewState.isShowSelected && deliveryOptionSelected && <DeliveryOptionSelected {...deliveryOptionSelected} />}
            {viewState.isShowSaveButton && saveButton}
            {viewState.isShowEditButton && editButton}
            {viewState.isShowCancelButton && cancelButton}
        </Module>
    );
};

export default CheckoutDeliveryOptionsView;